import { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { rtdb } from '@/config/firebase';

/**
 * Real-time listener for Ulgebra user credits from Firebase RTDB.
 * Path: ualic_user_credits/{uid}
 *
 * By default the balance is rounded UP to one decimal for display. Pass
 * `{ exact: true }` for the stored number, read the way the server reads it
 * (anything but a finite number counts as 0) — needed for money checks.
 */
export function useRealtimeCredits(uid: string | null | undefined, options?: { exact?: boolean }) {
  const exact = options?.exact ?? false;
  const [credits, setCredits] = useState<{ uid: string; value: number } | null>(null);

  useEffect(() => {
    if (!uid) return;

    const creditsRef = ref(rtdb, `ualic_user_credits/${uid}`);
    const unsubscribe = onValue(creditsRef, (snapshot) => {
      const val = snapshot.val();
      if (exact) {
        setCredits({ uid, value: typeof val === 'number' && Number.isFinite(val) ? val : 0 });
        return;
      }
      setCredits({ uid, value: val ? Math.ceil(val * 10) / 10 : 0 });
    });

    return () => unsubscribe();
  }, [uid, exact]);

  return uid && credits?.uid === uid ? credits.value : null;
}
